// Epic 33.3 — inline rich-text editor per section block. Wraps Tiptap
// with a minimal toolbar (bold, italic, list, link). Auto-save is
// driven by the parent: this component reports HTML upward via
// onChange, and the parent debounces a network save on its own.
//
// Why a thin component (not a full feature-rich CMS editor): the AC
// asks for cursive / lists / links — Tiptap's StarterKit covers them
// out of the box. Anything heavier (tables, media embeds, code blocks)
// is YAGNI for a firm landing page.

import { useEffect } from "react";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Placeholder from "@tiptap/extension-placeholder";
import Link from "@tiptap/extension-link";

export type RichTextBlockProps = {
  // initialHTML is consumed only on first mount + when the `key` prop
  // forces a remount (parent passes `key={section.id}` so swapping
  // between blocks creates a fresh editor with the new content).
  initialHTML: string;
  placeholder?: string;
  onChange: (html: string) => void;
  // ariaLabel is required so screen readers can announce which block
  // is being edited.
  ariaLabel: string;
  // disabled freezes the editor (used during a publish to prevent
  // saving over the publish-in-flight state).
  disabled?: boolean;
};

export default function RichTextBlock({
  initialHTML,
  placeholder,
  onChange,
  ariaLabel,
  disabled,
}: RichTextBlockProps) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        // Drop heading levels we don't want users to wreck the brand
        // hierarchy with — h2 + h3 only.
        heading: { levels: [2, 3] },
      }),
      Placeholder.configure({ placeholder: placeholder ?? "" }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: { rel: "noopener noreferrer", target: "_blank" },
      }),
    ],
    content: initialHTML || "",
    editable: !disabled,
    onUpdate({ editor: e }) {
      const html = e.getHTML();
      // Tiptap emits "<p></p>" for an empty doc — collapse that
      // upward so the parent state can treat empty as empty.
      onChange(html === "<p></p>" ? "" : html);
    },
  });

  // Keep `editable` in sync with disabled (Tiptap doesn't react to
  // prop changes after init).
  useEffect(() => {
    editor?.setEditable(!disabled);
  }, [editor, disabled]);

  if (!editor) {
    return <div className="rounded border border-slate-200 bg-slate-50 p-3 text-sm text-slate-400">…</div>;
  }

  const button = (label: string, active: boolean, onClick: () => void, testId?: string) => (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      aria-pressed={active}
      className={`rounded border px-2 py-1 text-xs ${
        active
          ? "border-brand-700 bg-brand-50 text-brand-900"
          : "border-slate-300 text-slate-700 hover:bg-slate-50"
      }`}
    >
      {label}
    </button>
  );

  return (
    <div
      className="rounded-md border border-slate-200 bg-white"
      data-testid="rich-text-block"
    >
      <div className="flex flex-wrap gap-1 border-b border-slate-200 bg-slate-50 px-2 py-1">
        {button("B", editor.isActive("bold"), () => editor.chain().focus().toggleBold().run(), "rtb-bold")}
        {button("I", editor.isActive("italic"), () => editor.chain().focus().toggleItalic().run(), "rtb-italic")}
        {button("H2", editor.isActive("heading", { level: 2 }), () =>
          editor.chain().focus().toggleHeading({ level: 2 }).run()
        )}
        {button("H3", editor.isActive("heading", { level: 3 }), () =>
          editor.chain().focus().toggleHeading({ level: 3 }).run()
        )}
        {button("• List", editor.isActive("bulletList"), () =>
          editor.chain().focus().toggleBulletList().run()
        )}
        {button("1. List", editor.isActive("orderedList"), () =>
          editor.chain().focus().toggleOrderedList().run()
        )}
        {button(
          "Link",
          editor.isActive("link"),
          () => {
            const previous = editor.getAttributes("link")?.href as string | undefined;
            const url = window.prompt("URL", previous ?? "https://");
            if (url === null) return; // cancelled
            if (url === "") {
              editor.chain().focus().unsetLink().run();
              return;
            }
            editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
          },
          "rtb-link",
        )}
      </div>
      <EditorContent
        editor={editor}
        aria-label={ariaLabel}
        className="prose prose-sm max-w-none px-3 py-2 [&_.ProseMirror]:min-h-[3rem] [&_.ProseMirror]:outline-none"
      />
    </div>
  );
}
